"use client";

import { useState } from "react";
import Image from "next/image";
import { Dialog } from "@/components/ui/Dialog";
import { AnimatedSectionHeader } from "@/components/typography/AnimatedSectionHeader";
import { PageDecorLayer } from "@/components/decorative/PageDecorLayer";
import { Reveal, Stagger, StaggerItem } from "@/components/motion";
import type { AboutGalleryContent } from "@/lib/api-types";
import { cn } from "@/lib/utils";

interface AboutFieldGallerySectionProps {
  gallery: AboutGalleryContent;
}

export function AboutFieldGallerySection({
  gallery,
}: AboutFieldGallerySectionProps) {
  const [selected, setSelected] = useState<string | null>(null);
  const photos = [...gallery.images]
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .slice(0, 4);

  return (
    <section className="site-section relative">
      <PageDecorLayer variant="section" palette="site" />
      <div className="relative z-10 mx-auto max-w-7xl px-4 md:px-6">
        <Reveal>
          <AnimatedSectionHeader
            kineticTrigger="mount"
            title={gallery.title}
            subtitle={gallery.subtitle}
          />
        </Reveal>
        <Stagger className="grid grid-cols-2 gap-3 md:grid-cols-4">
          {photos.map((photo, index) => (
            <StaggerItem key={photo.id}>
              <button
                type="button"
                onClick={() => setSelected(photo.src)}
                className="site-card group relative aspect-[4/3] w-full overflow-hidden p-0 text-left"
                aria-label={`View field photo ${index + 1}`}
              >
                <Image
                  src={photo.src}
                  alt={photo.alt || `Community field work ${index + 1}`}
                  fill
                  className={cn(
                    "object-cover transition-transform duration-500 ease-[var(--ease-out-expo)]",
                    "group-hover:scale-105"
                  )}
                  sizes="(max-width:768px) 50vw, 25vw"
                />
                <span className="absolute inset-0 bg-black/0 transition-colors group-hover:bg-black/20" />
              </button>
            </StaggerItem>
          ))}
        </Stagger>
      </div>

      <Dialog
        open={selected !== null}
        onClose={() => setSelected(null)}
        title={gallery.title}
        className="max-w-3xl border-[var(--site-border)] bg-[var(--site-surface)]"
      >
        {selected && (
          <div className="relative aspect-[4/3] w-full overflow-hidden rounded-xl">
            <Image
              src={selected}
              alt="Community field work"
              fill
              className="object-cover"
              sizes="(max-width:768px) 100vw, 768px"
            />
          </div>
        )}
      </Dialog>
    </section>
  );
}
